import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  HelpCircle,
  DollarSign,
  Share2,
  XCircle,
  Menu,
  LogOut,
  X,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../../components/ui/alert-dialog";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { logout } from "../../../slices/authSlice";
import { MdPayment } from "react-icons/md";

export default function AdminLayout() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuItems = [
    { icon: Users, label: "User Management", to: "/admin/users" },
    { icon: Briefcase, label: "Services Management", to: "/admin/services" },
    { icon: XCircle, label: "Cancelled Services", to: "/admin/cancelled-services" },
    { icon: HelpCircle, label: "Queries Management", to: "/admin/queries" },
    { icon: Share2, label: "Referer Management", to: "/admin/referrers" },
    { icon: DollarSign, label: "Financial Management", to: "/admin/financials" },
    { icon: MdPayment, label: "Stripe Jobs", to: "/admin/stripe-jobs" },
  ];

  const Sidebar = (
    <aside
      className={clsx(
        "flex flex-col justify-between bg-white shadow-lg border-r border-neutral-200 transition-all duration-300 z-40",
        collapsed ? "w-[5rem]" : "w-[16rem]",
        "h-screen fixed md:relative"
      )}
    >
      {/* Header */}
      <div className={clsx("flex items-center p-4 border-b border-neutral-200", collapsed ? "justify-center" : "justify-between")}>
        {!collapsed && (<div className="flex items-center gap-2 min-w-0">
          <LayoutDashboard className="text-primary flex-shrink-0" size={28} />
          <span className="text-xl font-semibold tracking-wide text-primary truncate">
            Admin Panel
          </span>
        </div>)}
        <Button
          variant="ghost"
          size="icon"
          className="text-neutral-500 hover:text-primary hidden md:flex flex-shrink-0"
          onClick={() => setCollapsed(!collapsed)}
        >
          <Menu size={22} />
        </Button>
        {/* Mobile Close */}
        <Button
          variant="ghost"
          size="icon"
          className="text-neutral-500 hover:text-primary md:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <X size={22} />
        </Button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-3 py-6 space-y-1">
        {menuItems.map(({ icon: Icon, label, to }) => {
          const isActive = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              className={clsx(
                "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-neutral-600 hover:bg-neutral-100",
                collapsed && "justify-center"
              )}
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={20} className="flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-neutral-200">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full px-3 py-2 flex justify-center gap-2"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>Logout</span>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Logout</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to log out?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { dispatch(logout()); navigate("/"); }}>
                Logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">{Sidebar}</div>

      {/* Mobile Sidebar */}
      <div
        className={clsx(
          "fixed inset-0 bg-black/50 z-30 transition-opacity duration-300 md:hidden",
          mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
        onClick={() => setMobileOpen(false)}
      ></div>

      <div
        className={clsx(
          "fixed top-0 left-0 h-full bg-white z-40 w-[16rem] shadow-lg transform transition-transform duration-300 md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {Sidebar}
      </div>

      {/* Right Content Area */}
      <div className="flex flex-col flex-1 h-full overflow-y-auto bg-neutral-50">
        {/* Mobile Menu Button */}
        <div className="md:hidden px-5 py-3 border-b border-neutral-200 bg-white sticky top-0 z-10">
          <Button
            variant="ghost"
            size="icon"
            className="text-neutral-600"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </Button>
        </div>

        {/* Main content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}